import { colors } from 'ui/theme';
import { ITERABLE_EVENTS } from 'constants/eventTypes';
import { match } from 'ts-pattern';
import { WhatsAppDataType } from 'modules/domains/segmentation/DataType';
import { RELATIVE_DATE_PRESETS, TODAY } from 'ui/datePicker/helpers';
import { HIGH_LEVEL_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';

export enum DeltaType {
  Positive = 'positive',
  Negative = 'negative',
  Neutral = 'neutral',
}

/**
 * Metrics where a decrease is considered positive (e.g., bounce rate, complaints).
 * For these metrics, we invert the color scheme (positive delta → negative color)
 * while keeping the trend arrow aligned with the actual delta direction.
 */
export const REVERSE_POSITIVE_TREND_METRICS = [
  ITERABLE_EVENTS.Bounce,
  ITERABLE_EVENTS.Complaint,
  ITERABLE_EVENTS.UnSubscribe,
  ITERABLE_EVENTS.HostedUnsubscribeClick,
  ITERABLE_EVENTS.SendSkip,
  ITERABLE_EVENTS.PushBounce,
  ITERABLE_EVENTS.PushSendSkip,
  ITERABLE_EVENTS.InAppSendSkip,
  ITERABLE_EVENTS.InAppRecall,
  ITERABLE_EVENTS.PushUninstall,
  ITERABLE_EVENTS.SMSBounce,
  ITERABLE_EVENTS.SMSSendSkip,
  ITERABLE_EVENTS.WebPushSendSkip,
  WhatsAppDataType.WhatsAppBounce,
  WhatsAppDataType.WhatsAppSendSkip,
  HIGH_LEVEL_VALUES_TO_LABELS.TotalBounces,
  HIGH_LEVEL_VALUES_TO_LABELS.TotalUnsubscribes,
];

export const getMetricDeltaInfo = (delta: number, metric?: string) => {
  if (delta === 0) {
    return {
      deltaType: DeltaType.Neutral,
      isPositiveTrend: false,
      isNegativeTrend: false,
      isNeutralTrend: true,
    };
  }

  const isPositiveTrend = delta > 0;
  const isReversedDeltaMetric = REVERSE_POSITIVE_TREND_METRICS.includes(metric as string);

  /**
   * For normal metrics:
   * - Positive delta → Positive type
   * - Negative delta → Negative type
   *
   * For reversed metrics (e.g., bounce rate):
   * - Positive delta → Negative type (but isPositiveTrend=true)
   * - Negative delta → Positive type (but isPositiveTrend=false)
   */
  const deltaType = match({ isPositiveTrend, isReversedDeltaMetric })
    .with(
      { isPositiveTrend: true, isReversedDeltaMetric: false },
      { isPositiveTrend: false, isReversedDeltaMetric: true },
      () => DeltaType.Positive,
    )
    .otherwise(() => DeltaType.Negative);

  return {
    deltaType,
    isPositiveTrend,
    isNegativeTrend: !isPositiveTrend,
    isNeutralTrend: false,
  };
};

export const DELTA_TYPE_TO_COLOR_MAP = {
  [DeltaType.Positive]: {
    textColor: colors.green100,
    chartColor: colors.green100,
  },
  [DeltaType.Negative]: {
    textColor: colors.textWarning,
    chartColor: colors.mustard50,
  },
  [DeltaType.Neutral]: {
    textColor: colors.textSecondary,
    chartColor: colors.borderStrong,
  },
};

export const getTimePeriodText = (dateRangePreset: string) => {
  const dateRangePresetLabel =
    RELATIVE_DATE_PRESETS[dateRangePreset as keyof typeof RELATIVE_DATE_PRESETS]?.menuLabel;

  if (!dateRangePresetLabel) return 'from previous period';
  return dateRangePreset === TODAY ? 'from yesterday' : `in ${dateRangePresetLabel.toLowerCase()}`;
};
